// CGamePreviewController — 預覽模式（?preview=1）控制器
// 從 game-scene.ts 抽出，把 UAT 截圖驗收用的「靜態畫面 + 互動 mock」邏輯整套包起來。
//
// 預覽模式特性：
// - 不啟動 CRound（AI 不動、不發牌）
// - 直接注入一份固定 mock 狀態到所有 UI 元件（4 家手牌 / 海底 / 計數 / 選牌 / 按鈕）
// - 玩家可點手牌切換選取、點預覽確認「打牌」（mock 邏輯：從 hand 移除 + 追加到海底）
// - 按 Page Down 輪換莊家（rerender 整組座位徽章 + 中央局風更新）
// - URL 含 ?activeSeat=0..3 指定哪家用 active 風字（高亮樣式，預設 0），確定性方便回歸截圖
//
// === 使用圖片 ===
// 無 — 浮水印是純文字；mock 資料只是 TTile / TMeld 物件，實際渲染由 deps 內的 UI 元件處理

import type Phaser from 'phaser';

import type { THand } from '@core/hand';
import { MeldType } from '@core/meld';
import type { TTile } from '@core/tile';
import { Suit, Wind, type TSeat, type TWind } from '@core/types';
import {
  ActionKey,
  type CActionBar,
  type CActionIcon,
  type CDiscardAnnounce,
  type CDiscardPool,
  type CHandView,
  CKeyboardHintBox,
  type CSeatBadges,
  type CTableInfo,
  type CWallCounter,
  type ISelfDrawHandTuning,
  type TActionChoice,
} from '@ui/index';

import {
  adjustSelfDrawPreviewTuning,
  nextPreviewSeat,
  PreviewLayer,
  previewSeatLabel,
  SelfDrawTuningControl,
  type TSelfDrawTuningControl,
  type TPreviewLayer,
} from './game-preview-navigation';

export { PreviewLayer } from './game-preview-navigation';
export type { TPreviewLayer } from './game-preview-navigation';

// 鍵盤提示框（左上角）已抽到 CKeyboardHintBox（src/ui/keyboard-hint-box.ts），
// 本檔只負責決定要顯示哪些行 — 見 refreshHints()。

/**
 * 從 URL search params 解析預覽模式設定。
 * - `?preview=1` → 啟用
 * - `?activeSeat=0..3` → 指定哪家用 active 風字（預設 0）
 * 在非瀏覽器環境（測試）回傳 enabled=false。
 */
export function readPreviewParamsFromUrl(): {
  enabled: boolean;
  activeSeat: TSeat;
  dealerSeat: TSeat;
  openingSeat: TSeat;
} {
  if (typeof window === 'undefined') {
    return { enabled: false, activeSeat: 0, dealerSeat: 0, openingSeat: 0 };
  }
  const params = new URLSearchParams(window.location.search);
  const enabled = params.get('preview') === '1';
  const raw = Number(params.get('activeSeat') ?? '0');
  const activeSeat = (Number.isInteger(raw) && raw >= 0 && raw <= 3 ? raw : 0) as TSeat;
  const parseSeat = (key: string, fallback: TSeat): TSeat => {
    const value = Number(params.get(key) ?? fallback);
    return (Number.isInteger(value) && value >= 0 && value <= 3 ? value : fallback) as TSeat;
  };
  return {
    enabled,
    activeSeat,
    dealerSeat: parseSeat('dealerSeat', 0),
    openingSeat: parseSeat('openingSeat', 0),
  };
}

/** Controller 對 scene 持有的依賴 — scene 端建立元件後一次注入。 */
export type TPreviewDeps = {
  readonly scene: Phaser.Scene;
  readonly playerSeat: TSeat;
  readonly handViews: readonly [CHandView, CHandView, CHandView, CHandView];
  readonly discardPool: CDiscardPool;
  readonly actionBar: CActionBar;
  readonly actionIcon: CActionIcon;
  readonly discardAnnounce: CDiscardAnnounce;
  readonly wallCounter: CWallCounter;
  readonly seatBadges: CSeatBadges;
  readonly tableInfo: CTableInfo;
  /** 重畫玩家手牌。 */
  readonly updatePlayerHandView: () => void;
  /** 重設海底時間序列（populate 時用整批寫入）。 */
  readonly setDiscardSequence: (seq: TTile[]) => void;
  /** 在海底序列尾端追加一張（玩家在預覽模式打牌時用）。 */
  readonly appendDiscard: (tile: TTile) => void;
  /** Controller 切換 dealerSeat 後通知 scene 重畫整組座位徽章。 */
  readonly rerenderSeatBadges: () => void;
  /** 啟動倒數計時（預覽模式 demo 用：populate 時開始，逾時由 scene 端負責 restart）。 */
  readonly startTurnTimer: () => void;
  /**
   * 進入逐步模式（按 S）：拆掉 mock 靜態狀態、啟動真實 CRound，但攔截自動排程，
   * 改由按 N 一步步推進；四家全由 AI 操作。
   */
  readonly startStepRound: () => void;
  /** 逐步模式（按 N）：推進一手 — 當前家 AI 出牌 + 自動結算吃碰反應，停在下一家 turn。 */
  readonly stepOnce: () => void;
  /** 離開逐步模式（在 Game 層按 ESC）：停掉逐步狀態，讓畫面回到靜態預覽。 */
  readonly exitStepRound: () => void;
  /** Preview 層按 N：把 active 風字切換到下一家（本家→下家→對家→上家→…循環）。 */
  readonly cyclePreviewHandSeat: () => void;
  /** SelfDraw 層：預覽指定座位的全畫面自摸儀式與整副手牌推倒。 */
  readonly previewSelfDraw: (seat: TSeat, tuning: ISelfDrawHandTuning) => void;
  /** SelfDraw 層調校按鍵：只重畫目前座位手牌，不重播全畫面儀式。 */
  readonly updateSelfDrawPreviewHand: (seat: TSeat, tuning: ISelfDrawHandTuning) => void;
  /** 離開 SelfDraw 層：停止仍在播放的自摸儀式。 */
  readonly cancelSelfDrawPreview: () => void;
  /** Preview 層按 H：預覽一炮多響的胡／放槍儀式，完成後點擊返回預覽。 */
  readonly previewDiscardWin: () => void;
  /** Preview 層按 L：預覽流局波浪、流局印記與點擊繼續。 */
  readonly previewGameDraw: () => void;
  /**
   * Preview 層按 F：預覽補花流程 — 臨時發一手「含花」的牌跑補花替換動畫，
   * 跑完呼叫 onDone（由 controller 還原靜態 mock）。
   */
  readonly previewFlowerPhase: (onDone: () => void) => void;
  /**
   * Preview 層按 D：預覽本家摸牌整理動畫（摸進 → 看一下 → 插入整理），
   * 跑完呼叫 onDone（由 controller 還原靜態 mock）。
   */
  readonly previewPlayerDraw: (onDone: () => void) => void;
  /**
   * Preview 層按 T：預覽聽牌動畫（風字聽圖示 + 橫幅飛入 + 一次瞇牌摸打），
   * 跑完呼叫 onDone（由 controller 還原靜態 mock）。
   */
  readonly previewTenpai: (onDone: () => void) => void;
  /**
   * Preview 層按 G：循環播放瞇牌動畫（含旋轉圓心紅點），方便邊播邊調手掌 / 拇指座標。
   * on=true 開始循環、on=false 停止（停止後 controller 會還原靜態 mock）。
   */
  readonly previewPeekLoop: (on: boolean) => void;
  /** Preview 層按 B：設定別家捨牌「門前預告」開關（切換顯示與否）。 */
  readonly setDiscardAnnounceEnabled: (on: boolean) => void;
};

/**
 * 預覽模式控制器。
 * Scene 在偵測到 ?preview=1 時建立一個實例，把所有 UI 元件 ref 透過 deps 傳入；
 * 後續預覽相關互動全部走 controller，scene 主流程只用 `if (this.preview)` 分流。
 */
export class CGamePreviewController {
  readonly activeSeat: TSeat;
  private dealerSeat: TSeat;
  private readonly openingSeat: TSeat;
  private mockPlayerHand: THand | null = null;
  private mockHands: [THand, THand, THand, THand] | null = null;
  private hintBox: CKeyboardHintBox | null = null; // 左上角鍵盤提示框（建構時間延遲到 populate）
  // 目前所在階層（巢狀狀態機）；每個階層只認得自己那層的快速鍵 + ESC 返回上一層
  private layer: TPreviewLayer = PreviewLayer.Preview;
  private discardAnnounceOn = true; // 別家捨牌門前預告開關（與 scene 預設一致）
  private peekLoopOn = false; // 瞇牌循環播放（按 G 切換）開關
  private selfDrawPreviewSeat: TSeat;
  private readonly selfDrawTunings: [
    ISelfDrawHandTuning,
    ISelfDrawHandTuning,
    ISelfDrawHandTuning,
    ISelfDrawHandTuning,
  ];

  constructor(
    activeSeat: TSeat,
    private readonly deps: TPreviewDeps,
    dealerSeat: TSeat = 0,
    openingSeat: TSeat = 0
  ) {
    this.activeSeat = activeSeat;
    this.dealerSeat = dealerSeat;
    this.openingSeat = openingSeat;
    this.selfDrawPreviewSeat = deps.playerSeat;
    this.selfDrawTunings = [
      deps.handViews[0].getSelfDrawHandTuning(),
      deps.handViews[1].getSelfDrawHandTuning(),
      deps.handViews[2].getSelfDrawHandTuning(),
      deps.handViews[3].getSelfDrawHandTuning(),
    ];
  }

  /**
   * 綁預覽模式專用熱鍵。Scene 在 create() 末段呼叫一次。
   *
   * 巢狀狀態機，每個階層只認得自己那層的鍵；ESC 一律返回上一層（見 PreviewLayer）：
   *
   *   Preview（根）  PageDown 切換莊家 / N 切換下一家(風字) / Z 進入自摸預覽 / H 預覽胡牌 / L 預覽流局 / F 預覽補花 / D 預覽摸牌整理 / T 預覽聽牌動畫 / G 瞇牌循環(調校座標) / C 四家吃碰槓圖示 / B 別家捨牌門前預告開關 / S 進入牌局 / A 進入 action bar
   *     ├─ SelfDraw        N 下一家自摸 / ESC 返回 Preview
   *     ├─ Game            N 下一手 / ESC 返回 Preview
   *     └─ ActionBar       E 展開 / ESC 返回 Preview
   *           └─ ActionBarExpanded  R 隨機模擬 0~3 顆吃+碰/槓/胡 / ESC 返回 ActionBar
   *
   * 註：N 在 Preview 層 = 切換下一家（風字高亮）、在 Game 層 = 下一手（同鍵依階層分流）。
   */
  bindKeyboard(): void {
    const kb = this.deps.scene.input.keyboard;
    if (!kb) return;
    kb.on('keydown-PAGE_DOWN', (event: KeyboardEvent) => {
      if (this.layer === PreviewLayer.Preview) {
        this.cycleDealer();
        this.refreshHints();
      } else if (this.layer === PreviewLayer.SelfDraw) {
        event.preventDefault();
        this.adjustSelfDrawTuning(SelfDrawTuningControl.DecreaseSpacing);
      }
    });
    kb.on('keydown-PAGE_UP', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(SelfDrawTuningControl.IncreaseSpacing);
    });
    kb.on('keydown-LEFT', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(SelfDrawTuningControl.Left);
    });
    kb.on('keydown-RIGHT', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(SelfDrawTuningControl.Right);
    });
    kb.on('keydown-UP', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(SelfDrawTuningControl.Up);
    });
    kb.on('keydown-DOWN', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(SelfDrawTuningControl.Down);
    });
    kb.on('keydown', (event: KeyboardEvent) => {
      if (this.layer !== PreviewLayer.SelfDraw) return;
      const control =
        event.key === '+'
          ? SelfDrawTuningControl.ScaleUp
          : event.key === '-'
            ? SelfDrawTuningControl.ScaleDown
            : null;
      if (!control) return;
      event.preventDefault();
      this.adjustSelfDrawTuning(control);
    });
    kb.on('keydown-S', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.enterGame();
    });
    kb.on('keydown-A', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.enterActionBar();
    });
    kb.on('keydown-Z', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.enterSelfDrawPreview();
    });
    kb.on('keydown-H', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.deps.previewDiscardWin();
    });
    kb.on('keydown-L', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.deps.previewGameDraw();
    });
    kb.on('keydown-F', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.deps.previewFlowerPhase(() => this.restoreMockState());
    });
    kb.on('keydown-D', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.deps.previewPlayerDraw(() => this.restoreMockState());
    });
    kb.on('keydown-T', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.deps.previewTenpai(() => this.restoreMockState());
    });
    kb.on('keydown-G', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      this.togglePeekLoop();
    });
    kb.on('keydown-C', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      // demo：在三家非本家座位淡入淡出顯示 吃 / 碰 / 槓 動作字樣
      this.previewActionIcons();
    });
    kb.on('keydown-B', () => {
      if (this.layer !== PreviewLayer.Preview) return;
      // 切換別家捨牌「門前預告」開關；開啟時 demo 在三家非本家風字處顯示要打的牌
      this.discardAnnounceOn = !this.discardAnnounceOn;
      this.deps.setDiscardAnnounceEnabled(this.discardAnnounceOn);
      if (this.discardAnnounceOn) this.previewDiscardAnnounce();
      this.refreshHints();
    });
    kb.on('keydown-E', () => {
      if (this.layer !== PreviewLayer.ActionBar) return;
      this.enterActionBarExpanded();
    });
    kb.on('keydown-R', () => {
      if (this.layer !== PreviewLayer.ActionBarExpanded) return;
      // 隨機模擬 0~3 顆吃 + 碰/槓/胡（驗收「多顆吃」與「只顯示有功能按鈕」）
      this.deps.actionBar.randomizePreview();
    });
    kb.on('keydown-N', () => {
      // N 依階層分流：Preview → 下一家風字；SelfDraw → 下一家自摸；Game → 下一手
      if (this.layer === PreviewLayer.Preview) {
        this.deps.cyclePreviewHandSeat();
      } else if (this.layer === PreviewLayer.SelfDraw) {
        this.selfDrawPreviewSeat = nextPreviewSeat(this.selfDrawPreviewSeat);
        this.playSelfDrawPreview();
        this.refreshHints();
      } else if (this.layer === PreviewLayer.Game) {
        this.deps.stepOnce();
        this.refreshHints();
      }
    });
    kb.on('keydown-ESC', () => {
      this.goBack();
    });
  }

  /** Preview 層按 G：切換瞇牌循環播放（調校手掌 / 拇指座標用）。 */
  private togglePeekLoop(): void {
    this.peekLoopOn = !this.peekLoopOn;
    this.deps.previewPeekLoop(this.peekLoopOn);
    if (!this.peekLoopOn) this.restoreMockState(); // 停止 → 還原靜態 mock
    this.refreshHints();
  }

  /** Preview → Game（按 S）：請 scene 啟動真實 CRound（四家全 AI），改用 N 推進。 */
  private enterGame(): void {
    // 進真實牌局前先停掉瞇牌循環，否則它會持續自我重排 delayedCall，
    // 反覆用 mock 手牌覆寫真實牌局畫面、與出牌動畫互搶 handView。
    if (this.peekLoopOn) this.togglePeekLoop();
    this.layer = PreviewLayer.Game;
    this.deps.startStepRound();
    this.refreshHints();
  }

  /** Preview → SelfDraw（按 Z）：從本家開始播放，之後可按 N 依序切換四家。 */
  private enterSelfDrawPreview(): void {
    this.layer = PreviewLayer.SelfDraw;
    this.selfDrawPreviewSeat = this.deps.playerSeat;
    this.playSelfDrawPreview();
    this.refreshHints();
  }

  /** 還原上一家攤牌，再重新播放目前座位的自摸儀式。 */
  private playSelfDrawPreview(): void {
    this.restoreMockHands();
    this.deps.previewSelfDraw(
      this.selfDrawPreviewSeat,
      this.selfDrawTunings[this.selfDrawPreviewSeat]
    );
  }

  /** 調整目前座位的自摸攤牌，立即重畫並更新左上數值。 */
  private adjustSelfDrawTuning(control: TSelfDrawTuningControl): void {
    const seat = this.selfDrawPreviewSeat;
    const tuning = adjustSelfDrawPreviewTuning(this.selfDrawTunings[seat], control);
    this.selfDrawTunings[seat] = tuning;
    this.deps.updateSelfDrawPreviewHand(seat, tuning);
    this.refreshHints();
  }

  /** demo（Preview 層按 C）：四家（含本家）同時淡入淡出 吃 / 碰 / 槓 / 槓 動作字樣。 */
  private previewActionIcons(): void {
    const melds = [MeldType.Chi, MeldType.Pong, MeldType.KongExposed, MeldType.KongConcealed];
    ([0, 1, 2, 3] as TSeat[]).forEach((s, i) =>
      this.deps.actionIcon.show(s, melds[i % melds.length]!)
    );
  }

  /** demo（Preview 層按 B 開啟時）：三家非本家風字處顯示「要打出的牌」門前預告。 */
  private previewDiscardAnnounce(): void {
    const player = this.deps.playerSeat;
    const sample = [
      mockTile(900, Suit.Man, 5),
      mockTile(901, Suit.Pin, 3),
      mockTile(902, Suit.Sou, 7),
    ];
    ([0, 1, 2, 3] as TSeat[])
      .filter((s) => s !== player)
      .forEach((s, i) => this.deps.discardAnnounce.show(s, sample[i % sample.length]!));
  }

  /** Preview → ActionBar（按 A）：顯示動作條（收合狀態）。 */
  private enterActionBar(): void {
    this.layer = PreviewLayer.ActionBar;
    this.deps.actionBar.setVisible(true); // setVisible 內部會 reset 成收合
    this.refreshHints();
  }

  /** ActionBar → ActionBarExpanded（按 E）：展開吃碰槓胡放棄。 */
  private enterActionBarExpanded(): void {
    this.layer = PreviewLayer.ActionBarExpanded;
    this.deps.actionBar.expand();
    this.refreshHints();
  }

  /** ESC：返回上一層（在根層 Preview 無動作）。 */
  private goBack(): void {
    switch (this.layer) {
      case PreviewLayer.SelfDraw:
        this.deps.cancelSelfDrawPreview();
        this.restoreMockHands();
        this.layer = PreviewLayer.Preview;
        break;
      case PreviewLayer.Game:
        this.deps.exitStepRound();
        this.restoreMockState(); // 真實牌局畫面覆寫回靜態 mock
        this.layer = PreviewLayer.Preview;
        break;
      case PreviewLayer.ActionBar:
        this.deps.actionBar.setVisible(false);
        this.layer = PreviewLayer.Preview;
        break;
      case PreviewLayer.ActionBarExpanded:
        this.deps.actionBar.collapse();
        this.layer = PreviewLayer.ActionBar;
        break;
      case PreviewLayer.Preview:
        if (this.peekLoopOn) this.togglePeekLoop(); // 瞇牌循環中 → ESC 停止
        return; // 已在最上層
    }
    this.refreshHints();
  }

  /** 依當前階層重新渲染左上角鍵盤提示框（第一行為麵包屑，標示目前深度）。 */
  private refreshHints(): void {
    if (!this.hintBox) return;
    this.hintBox.setLines(this.hintLinesForLayer());
  }

  /** 各階層的提示內容；第一行麵包屑用「›」串接，越深層越長。 */
  private hintLinesForLayer(): string[] {
    switch (this.layer) {
      case PreviewLayer.Preview:
        return [
          '預覽模式',
          'PageDown：切換莊家',
          'N：切換下一家（風字）',
          'Z：進入自摸預覽',
          'H：預覽胡／放槍動畫',
          'L：預覽流局動畫',
          'F：預覽補花流程',
          'D：預覽本家摸牌整理',
          'T：預覽聽牌動畫',
          `G：瞇牌循環（調校座標，${this.peekLoopOn ? '播放中' : '關'}）`,
          'C：四家吃碰槓圖示',
          `B：別家捨牌門前預告（${this.discardAnnounceOn ? '開' : '關'}）`,
          'S：開始牌局',
          'A：開啟 action bar',
        ];
      case PreviewLayer.SelfDraw: {
        const tuning = this.selfDrawTunings[this.selfDrawPreviewSeat];
        return [
          '預覽模式 › 自摸',
          `目前：${previewSeatLabel(this.selfDrawPreviewSeat, this.deps.playerSeat)}`,
          `X 位移：${this.formatSigned(tuning.offsetX)} px（← / →）`,
          `Y 位移：${this.formatSigned(tuning.offsetY)} px（↑ / ↓）`,
          `牌距：${tuning.tileSpacing.toFixed(2)} px（PageUp 拉開 / PageDown 拉近）`,
          `牌縮放：${tuning.tileScale.toFixed(3)}（+ / -）`,
          'N：下一家自摸',
          'ESC：返回預覽模式',
        ];
      }
      case PreviewLayer.Game:
        return ['預覽模式 › 牌局模式', 'N：下一手（AI 出牌，吃碰自動判斷）', 'ESC：返回預覽模式'];
      case PreviewLayer.ActionBar:
        return ['預覽模式 › action bar', 'E：展開 吃碰槓胡放棄', 'ESC：返回預覽模式'];
      case PreviewLayer.ActionBarExpanded:
        return [
          '預覽模式 › action bar › 展開',
          'R：隨機 0~3 顆吃 + 碰槓胡',
          'ESC：返回 action bar',
        ];
    }
  }

  private formatSigned(value: number): string {
    return value >= 0 ? `+${value}` : String(value);
  }

  getDealerSeat(): TSeat {
    return this.dealerSeat;
  }

  getMockHand(seat: TSeat = this.deps.playerSeat): THand | null {
    return this.mockHands?.[seat] ?? null;
  }

  /** 只重畫四家 mock 手牌，不重設海底、計時器與 action bar。 */
  private restoreMockHands(): void {
    if (!this.mockHands) return;
    this.mockPlayerHand = this.mockHands[this.deps.playerSeat];
    this.deps.updatePlayerHandView();
    ([0, 1, 2, 3] as TSeat[])
      .filter((seat) => seat !== this.deps.playerSeat)
      .forEach((seat) => this.deps.handViews[seat].update(this.mockHands![seat]));
  }

  /** 預覽模式 seatWinds：由 openingSeat 決定，與莊家分離。 */
  computeSeatWinds(): Record<TSeat, TWind> {
    const order: readonly TWind[] = [Wind.East, Wind.South, Wind.West, Wind.North];
    return {
      0: order[(0 - this.openingSeat + 4) % 4]!,
      1: order[(1 - this.openingSeat + 4) % 4]!,
      2: order[(2 - this.openingSeat + 4) % 4]!,
      3: order[(3 - this.openingSeat + 4) % 4]!,
    };
  }

  /** Page Down：莊家順時針輪換一家、座位徽章 + 中央局風重建。 */
  cycleDealer(): void {
    this.dealerSeat = ((this.dealerSeat + 1) % 4) as TSeat;
    this.deps.rerenderSeatBadges();
  }

  /** 玩家在預覽模式按下確認 → 從 mock 手牌移除這張、追加到海底。 */
  discardMockTile(tile: TTile): void {
    if (!this.mockPlayerHand) return;
    this.mockPlayerHand = {
      ...this.mockPlayerHand,
      concealed: this.mockPlayerHand.concealed.filter((t) => t.id !== tile.id),
    };
    if (this.mockHands) this.mockHands[this.deps.playerSeat] = this.mockPlayerHand;
    this.deps.appendDiscard(tile);
    this.deps.updatePlayerHandView();
  }

  /**
   * Scene 在 create() 偵測到預覽模式時呼叫一次：注入 mock 狀態 + 建立左上提示框。
   * 之後互動由 discardMockTile / cycleDealer / 階層切換處理。
   */
  populate(): void {
    this.restoreMockState();
    this.hintBox = new CKeyboardHintBox(this.deps.scene);
    this.refreshHints();
  }

  /**
   * 注入（或重置回）完整的靜態 mock 狀態到所有 UI 元件。
   * 由 populate() 初次呼叫，亦由「Game 層按 ESC 返回 Preview」時呼叫，
   * 把真實牌局畫面覆寫回靜態 mock。不建立提示框（提示框只建一次，避免 DOM 重複）。
   */
  restoreMockState(): void {
    const { discardPool, actionBar, wallCounter } = this.deps;

    // ----- 4 家手牌（preview 用，純畫面測試，無遊戲邏輯）-----
    // 玩家自家：17 張暗牌 + 2 組副露（meld-bottom）+ 1 張花
    const playerHand: THand = {
      concealed: [
        mockTile(0, Suit.Man, 1),
        mockTile(1, Suit.Man, 3),
        mockTile(2, Suit.Man, 5),
        mockTile(3, Suit.Man, 6),
        mockTile(4, Suit.Man, 6),
        mockTile(5, Suit.Pin, 1),
        mockTile(6, Suit.Pin, 2),
        mockTile(7, Suit.Pin, 3),
        mockTile(8, Suit.Pin, 7),
        mockTile(9, Suit.Sou, 4),
        mockTile(10, Suit.Sou, 5),
        mockTile(11, Suit.Sou, 6),
        mockTile(12, Suit.Sou, 7),
        mockTile(13, Suit.Sou, 8),
        mockTile(14, Suit.Sou, 9),
        mockTile(15, Suit.Wind, 1),
        mockTile(16, Suit.Wind, 3),
      ],
      melds: [
        mockPong(200, Suit.Dragon, 1), // 中×3
        mockKong(210, Suit.Wind, 1), // 東×4（自家莊家，槓東）
      ],
      flowers: [mockTile(160, Suit.Flower, 1), mockTile(161, Suit.Flower, 5)],
    };
    // 其他 3 家：16 張暗牌 + 1 組碰 + 1 組槓（各家槓一個風牌）
    const aiHand = (
      idStart: number,
      pong: { suit: TTile['suit']; rank: number },
      kong: { suit: TTile['suit']; rank: number },
      flowerRanks: number[] // 補的花牌 rank（春1夏2秋3冬4梅5蘭6菊7竹8）
    ): THand => ({
      concealed: Array.from({ length: 16 }, (_, i) => mockTile(idStart + i, Suit.Man, 1)),
      melds: [
        mockPong(idStart + 100, pong.suit, pong.rank),
        mockKong(idStart + 110, kong.suit, kong.rank),
      ],
      flowers: flowerRanks.map((r, i) => mockTile(idStart + 120 + i, Suit.Flower, r)),
    });

    // 右家：碰中 + 槓南 + 花（春夏秋）
    const rightHand = aiHand(
      20,
      { suit: Suit.Dragon, rank: 1 },
      { suit: Suit.Wind, rank: 2 },
      [1, 2, 3]
    );
    // 對家：碰發 + 槓西 + 花（梅蘭）
    const oppositeHand = aiHand(
      40,
      { suit: Suit.Dragon, rank: 2 },
      { suit: Suit.Wind, rank: 3 },
      [5, 6]
    );
    // 左家：碰白 + 槓北 + 花（春夏秋冬）
    const leftHand = aiHand(
      60,
      { suit: Suit.Dragon, rank: 3 },
      { suit: Suit.Wind, rank: 4 },
      [1, 2, 3, 4]
    );
    this.mockHands = [playerHand, rightHand, oppositeHand, leftHand];
    this.restoreMockHands();

    // ----- 海底捨牌：鋪滿 4 行（每行 18 張）的「時間排序」序列，驗收多行海底排版 -----
    // 用迴圈循環各花色 + rank 產生變化；id 用 1000+ 避免與其他 mock 牌衝突。
    const PREVIEW_DISCARD_FULL_ROWS = 3; // 前 3 行鋪滿
    const PREVIEW_DISCARD_LAST_ROW = 9; // 第 4 行張數（半行，呈現對局進行中的樣子）
    const PREVIEW_DISCARD_TILES_PER_ROW = 18; // 與 CDiscardPool TILES_PER_ROW 一致
    const discardSuitCycle = [Suit.Man, Suit.Pin, Suit.Sou, Suit.Wind, Suit.Dragon] as const;
    const discardCount =
      PREVIEW_DISCARD_TILES_PER_ROW * PREVIEW_DISCARD_FULL_ROWS + PREVIEW_DISCARD_LAST_ROW;
    const initialDiscards: TTile[] = Array.from({ length: discardCount }, (_, i) => {
      const suit = discardSuitCycle[i % discardSuitCycle.length]!;
      const maxRank = suit === Suit.Wind ? 4 : suit === Suit.Dragon ? 3 : 9;
      return mockTile(1000 + i, suit, (i % maxRank) + 1);
    });
    this.deps.setDiscardSequence(initialDiscards);
    // 示範「被吃/碰/槓」覆蓋圖：標記幾張海底牌為 claimed（牌留著、蓋上深綠覆蓋圖）
    const claimedIds = new Set<number>([1000 + 5, 1000 + 14, 1000 + 33, 1000 + 50]);
    discardPool.update(initialDiscards, { claimedIds });

    // ----- 剩牌計數 -----
    wallCounter.setRemainingWall(52);

    // ----- 倒數計時：預覽模式持續顯示，逾時 scene 端會自動 restart -----
    this.deps.startTurnTimer();

    // 單擊出牌模式：靜態預覽不再預選牌；要看出牌動畫直接點本家手牌即可。

    // ----- 動作按鈕（示意）：用 setChoices 給一組「多顆吃 + 碰/多組槓/胡 + 放棄」mock，
    //       含 2 顆吃與 2 顆槓，驗收「只顯示有功能按鈕」、多候選與按鈕上方迷你牌；
    //       整條仍保持隱藏，使用者按 A 顯示、按 E 展開、按 R 重抽 0~3 顆吃。-----
    actionBar.clear();
    const mockChoices: TActionChoice[] = [
      // 吃①：索3-4-5（標記被吃的索5）
      {
        key: ActionKey.Chi,
        handler: () => {},
        tiles: [mockTile(300, Suit.Sou, 3), mockTile(301, Suit.Sou, 4), mockTile(302, Suit.Sou, 5)],
        highlightTileId: 302,
      },
      // 吃②：索5-6-7（標記被吃的索5）
      {
        key: ActionKey.Chi,
        handler: () => {},
        tiles: [mockTile(303, Suit.Sou, 5), mockTile(304, Suit.Sou, 6), mockTile(305, Suit.Sou, 7)],
        highlightTileId: 303,
      },
      // 碰=中中+被碰的中(標記)、明槓=東東東+被槓的東(標記)、胡=筒8(標記)
      {
        key: ActionKey.Pong,
        handler: () => {},
        tiles: [
          mockTile(306, Suit.Dragon, 1),
          mockTile(307, Suit.Dragon, 1),
          mockTile(312, Suit.Dragon, 1),
        ],
        highlightTileId: 312,
      },
      {
        key: ActionKey.Kong,
        handler: () => {},
        tiles: [
          mockTile(308, Suit.Wind, 1),
          mockTile(309, Suit.Wind, 1),
          mockTile(310, Suit.Wind, 1),
          mockTile(313, Suit.Wind, 1),
        ],
        highlightTileId: 313,
      },
      // 暗槓候選②：多組暗槓時必須各自顯示，不標記外來牌
      {
        key: ActionKey.Kong,
        handler: () => {},
        tiles: [
          mockTile(314, Suit.Pin, 6),
          mockTile(315, Suit.Pin, 6),
          mockTile(316, Suit.Pin, 6),
          mockTile(317, Suit.Pin, 6),
        ],
      },
      {
        key: ActionKey.Hu,
        handler: () => {},
        tiles: [mockTile(311, Suit.Pin, 8)],
        highlightTileId: 311,
      },
      { key: ActionKey.Pass, handler: () => {} },
    ];
    actionBar.setChoices(mockChoices);
    // 注意：clear() 已把整條隱藏 — 不在這裡 setVisible(true)，等 A 鍵進入 ActionBar 層
  }
}

// ============================================================================
// Mock fixtures — 純資料 builders（preview only，不入 CRound）
// ============================================================================

/** 預覽模式專用：建立一個 TTile（純 mock，不入 CRound）。 */
function mockTile(id: number, suit: TTile['suit'], rank: number): TTile {
  return { id, suit, rank };
}

/** 預覽模式專用：建立一組碰 TMeld（3 張同種牌，純 mock）。 */
function mockPong(idStart: number, suit: TTile['suit'], rank: number) {
  return {
    type: MeldType.Pong,
    tiles: [
      mockTile(idStart, suit, rank),
      mockTile(idStart + 1, suit, rank),
      mockTile(idStart + 2, suit, rank),
    ],
  };
}

/** 預覽模式專用：建立一組明槓 TMeld（4 張同種牌，純 mock）。 */
function mockKong(idStart: number, suit: TTile['suit'], rank: number) {
  return {
    type: MeldType.KongExposed,
    tiles: [
      mockTile(idStart, suit, rank),
      mockTile(idStart + 1, suit, rank),
      mockTile(idStart + 2, suit, rank),
      mockTile(idStart + 3, suit, rank),
    ],
  };
}
